import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BadgeCheck,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Star,
  Calendar,
} from "lucide-react";
import { api } from "../lib/api";
import { countryFlag } from "../lib/format";
import VehicleCard from "../components/VehicleCard";

export default function DealerDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [dealer, setDealer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, vRes] = await Promise.all([
          api.get(`/dealers/${id}`),
          api.get(`/vehicles`, { params: { limit: 100 } }),
        ]);
        setDealer(dRes.data);
        setVehicles((vRes.data.items || []).filter((v) => v.dealer_id === id));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!dealer) {
    return <div className="py-20 text-center text-sm text-slate-500">404</div>;
  }

  return (
    <div className="py-6 md:py-10" data-testid={`dealer-detail-${dealer.id}`}>
      <Link
        to="/marketplace"
        data-testid="dealer-back"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("dealer.back")}
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-2xl bg-[#0A0A0B] text-white p-6 md:p-10"
      >
        <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -left-20 -top-20 w-72 h-72 rounded-full bg-emerald-500/12 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-3">
              {countryFlag(dealer.country)} {dealer.city} · {dealer.country}
              {dealer.verified && (
                <span className="ml-2 inline-flex items-center gap-1 text-emerald-400">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  {t("dealer.verified")}
                </span>
              )}
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter leading-[1.02]">
              {dealer.name}
            </h1>
            <p className="mt-3 max-w-2xl text-slate-300 text-sm leading-relaxed">
              {dealer.description}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 md:min-w-[320px]">
            <Metric icon={Star} label={t("dealer.rating")} value={dealer.rating.toFixed(1)} />
            <Metric icon={Calendar} label={t("dealer.years")} value={`${dealer.years_active}y`} />
            <Metric label={t("dealer.reviews")} value={dealer.reviews_count} />
          </div>
        </div>
        <div className="relative z-10 mt-6 flex flex-wrap gap-3">
          <a
            data-testid="dealer-phone"
            href={`tel:${dealer.phone}`}
            className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-4 py-2 text-sm font-semibold hover:bg-slate-100 transition-colors"
          >
            <Phone className="w-4 h-4" /> {dealer.phone}
          </a>
          <a
            data-testid="dealer-email"
            href={`mailto:${dealer.email}`}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/20 transition-colors"
          >
            <Mail className="w-4 h-4" /> {dealer.email}
          </a>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm text-slate-300">
            <MapPin className="w-4 h-4" /> {dealer.address}
          </div>
        </div>
      </motion.div>

      {/* Inventory */}
      <section className="mt-10" data-testid="dealer-inventory">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-950">
            {t("dealer.inventory_title")}
          </h2>
          <div className="text-xs text-slate-500 font-medium">
            {t("marketplace.count_result", { count: vehicles.length })}
          </div>
        </div>
        {vehicles.length === 0 ? (
          <div className="text-center py-14 text-slate-500 text-sm">
            {t("dealer.no_inventory")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {vehicles.map((v) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="text-center rounded-xl bg-white/5 border border-white/10 py-3 px-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
        {label}
      </div>
      <div className="mt-1 font-heading font-bold text-lg tabular-nums inline-flex items-center gap-1">
        {Icon && <Icon className="w-4 h-4 text-blue-400" />} {value}
      </div>
    </div>
  );
}
